// The numbers a Library upload row shows, in the reader's language. Sizes are decimal (kB, MB), as the 500 MB limit
// is stated; units come from Intl, never from a hand-built string.
const KB = 1_000;
const MB = 1_000_000;

function unitFormat(locale: string, unit: 'kilobyte' | 'megabyte', digits: number) {
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

const scale = (bytes: number) =>
  bytes < MB
    ? { unit: 'kilobyte' as const, divisor: KB, digits: 0 }
    : { unit: 'megabyte' as const, divisor: MB, digits: bytes < 100 * MB ? 1 : 0 };

/** "4.8 MB", "612 MB", "820 kB". */
export function sizeLabel(bytes: number, locale: string): string {
  const { unit, divisor, digits } = scale(bytes);
  return unitFormat(locale, unit, digits).format(Math.max(bytes, 0) / divisor);
}

/** "2.9" and "4.8 MB": the part sent, in the unit of the whole, and the whole. */
export function progressSizes(
  loaded: number,
  total: number,
  locale: string,
): { loaded: string; total: string } {
  const { divisor, digits } = scale(total);
  return {
    loaded: new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(Math.min(Math.max(loaded, 0), total) / divisor),
    total: sizeLabel(total, locale),
  };
}

/** "6 seconds", "3 minutes", "2 hours": how long is left, in whole units. */
export function durationLabel(seconds: number, locale: string): string {
  const whole = Math.max(1, Math.ceil(seconds));
  const [unit, value] =
    whole < 60
      ? (['second', whole] as const)
      : whole < 3600
        ? (['minute', Math.round(whole / 60)] as const)
        : (['hour', Math.round(whole / 3600)] as const);
  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'long' }).format(value);
}

/** The file name's extension, upper-cased ("Q4 sales.xlsx" → "XLSX"); empty when there is none. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  const value = dot > 0 ? name.slice(dot + 1) : '';
  return /^[A-Za-z0-9]{1,5}$/u.test(value) ? value.toUpperCase() : '';
}
